/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {Subscription} from 'rxjs';
import {SpinnerService} from '../../../../spinner/spinner.service';
import {NgbDropdown} from '@ng-bootstrap/ng-bootstrap';
import {ReferenceDataModel} from '../../../../../api/reference-data.model';
import {StatefulComponent} from '../../../../stateful/stateful.component';
import {MultiSelectDropdownState} from './models/multi-select-dropdown.state';
import {MultiSelectDropdownOptionModel} from './models/multi-select-dropdown-option.model';
import {MultiSelectDropdownConfiguration} from './configuration/multi-select-dropdown.configuration';

@Component({
    selector: 'app-multi-select-dropdown',
    templateUrl: './multi-select-dropdown.component.html',
    styleUrls: [
        '../../../shared/styles/select-dropdown.css',
        './multi-select-dropdown.component.css'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MultiSelectDropdownComponent extends StatefulComponent<MultiSelectDropdownState> implements OnInit, OnDestroy, OnChanges {

    @ViewChild('dropdown') dropdown: NgbDropdown;

    private readonly LOCAL_LOADING_INSTANCE_KEY = 'DROPDOWN_CONTENT_LOADING';
    public spinnerObservable = this.spinnerService.onLocalChange(this.LOCAL_LOADING_INSTANCE_KEY);
    private subscriptions = new Subscription();

    @Input() options: ReferenceDataModel[];
    @Input() dropdownOpenMenu: EventEmitter<boolean>;
    @Input() deleteSelectedOption: EventEmitter<string>;
    @Input() multiSelectDropdownLabel: string;
    @Output() addSelectedOption = new EventEmitter<ReferenceDataModel>();

    constructor(public spinnerService: SpinnerService) {
        super();
    }

    ngOnInit(): void {
        this.storeState({
            selectedOptionsCodes: []
        });
        this.subscriptions.add(this.deleteSelectedOption?.subscribe((code: string) => {
            this.unselectDropdownItem(code);
        }));
        this.subscriptions.add(this.dropdownOpenMenu?.subscribe((open: boolean) => {
            open ? this.dropdown.open() : this.dropdown.close();
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['options']) {
            const dropdownData: MultiSelectDropdownOptionModel[] = this.options?.map((elem) => ({
                code: elem.code,
                name: elem.name,
                active: elem.active,
                selected: this.state.selectedOptionsCodes?.includes(elem.code)
            }));
            this.storeState({
                dropdownData: dropdownData
            });
        }
    }

    dropdownItemDataCy(itemValue: string, type: string): string {
        return itemValue && type && `dropdown-item-${type}-${itemValue.toLowerCase()
                .replace('(', '')
                .replace(')', '')
                .replace(new RegExp(/\s/g), '-')}`;
    }

    selectDropdownItem(option: ReferenceDataModel): void {
        const currentSelectedOptionsCodes = [...this.state.selectedOptionsCodes];
        currentSelectedOptionsCodes.push(option.code);
        this.storeState({
            selectedOptionsCodes: currentSelectedOptionsCodes
        });
        this.updateDropdownOption(option.code, true);
        this.addSelectedOption.emit(option);
    }

    private unselectDropdownItem(code: string): void {
        const currentSelectedOptionsCodes = [...this.state.selectedOptionsCodes].filter((elem) => elem !== code);
        this.storeState({
            selectedOptionsCodes: currentSelectedOptionsCodes
        });
        this.updateDropdownOption(code, false);
    }

    private updateDropdownOption(code: string, selected: boolean): void {
        if (this.state.dropdownData != null) {
            const dropdownDataCopy = [...this.state?.dropdownData];
            const dropdownOption = dropdownDataCopy.find((elem) => elem.code === code);

            if (dropdownOption) {
                dropdownOption.selected = selected;
            }

            this.storeState({
                dropdownData: dropdownDataCopy
            });
        }

    }
}
